import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@paribus/shared-ui";
import { Check, ChevronDown, ChevronsUpDown, Loader2, Plus } from "lucide-react";
import React, { type ReactNode } from "react";

import type { PickerItem } from "./types";

export interface PickerViewProps {
  filteredItems: PickerItem[];
  value: string | null;
  open: boolean;
  triggerText: string;
  isLoading: boolean;
  disabled: boolean;
  groups: boolean;
  groupOrder?: string[];
  commandEmptyLabel: string;
  searchPlaceholder: string;
  triggerChevronVariant: "chevrons" | "chevron";
  popoverModal: boolean;
  trigger?: ReactNode;
  contentClassName?: string;
  triggerVariant: "default" | "ghost";
  showCreate: boolean;
  createLabel: string;
  commandResetNonce: number;
  holdOpen: boolean;
  onOpenChange: (nextOpen: boolean) => void;
  onQueryChange: (nextQuery: string) => void;
  onSelect: (itemValue: string) => void;
  onCreate: () => Promise<void>;
}

export const PickerView = ({
  filteredItems,
  value,
  open,
  triggerText,
  isLoading,
  disabled,
  groups,
  groupOrder,
  commandEmptyLabel,
  searchPlaceholder,
  triggerChevronVariant,
  popoverModal,
  trigger,
  contentClassName,
  triggerVariant,
  showCreate,
  createLabel,
  commandResetNonce,
  holdOpen,
  onOpenChange,
  onQueryChange,
  onSelect,
  onCreate,
}: PickerViewProps) => {
  const renderItem = (item: PickerItem) => (
    <CommandItem key={item.value} value={item.value} onSelect={() => onSelect(item.value)}>
      <Check className={cn("mr-2 h-4 w-4", item.value === value ? "opacity-100" : "opacity-0")} />
      <span className="flex-1 truncate">{item.label}</span>
    </CommandItem>
  );

  const renderItems = () => {
    if (!groups) {
      return filteredItems.map(renderItem);
    }

    const buckets: Record<string, PickerItem[]> = {};
    for (const item of filteredItems) {
      const key = item.group ?? "";
      (buckets[key] ??= []).push(item);
    }

    const groupKeys = Object.keys(buckets);
    const normalizedOrder = (groupOrder ?? []).filter((key) => groupKeys.includes(key));
    const orderedSet = new Set(normalizedOrder);
    const remaining = groupKeys.filter((key) => !orderedSet.has(key));

    // If there are ungrouped items (`group === undefined`), render them first (no heading).
    const ungroupedItems = buckets[""] ?? [];
    const orderedGroups = [...normalizedOrder, ...remaining.filter((k) => k !== "")];

    return (
      <>
        {ungroupedItems.map(renderItem)}
        {orderedGroups.map((groupKey) => {
          const groupItems = buckets[groupKey] ?? [];
          if (groupItems.length === 0) return null;
          return (
            <CommandGroup key={groupKey} heading={groupKey}>
              {groupItems.map(renderItem)}
            </CommandGroup>
          );
        })}
      </>
    );
  };

  const defaultTriggerClassName =
    "flex w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50";

  const ghostTriggerClassName = cn(
    "inline-flex w-fit max-w-full items-center gap-1 rounded-full px-2.5 py-1 text-sm hover:bg-surface-sunken disabled:cursor-not-allowed disabled:opacity-50",
    value === null && "border border-dashed border-input text-muted-foreground"
  );

  const renderTrigger = () => {
    if (triggerVariant === "ghost") {
      return (
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-label={triggerText}
          aria-busy={isLoading || undefined}
          disabled={disabled}
          className={ghostTriggerClassName}
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
          ) : null}
          <span className="min-w-0 truncate">{triggerText}</span>
          <ChevronDown size={14} className="shrink-0 text-text-disabled" />
        </button>
      );
    }

    return (
      <button
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-label={triggerText}
        aria-busy={isLoading || undefined}
        disabled={disabled}
        className={defaultTriggerClassName}
      >
        <span className="flex min-w-0 items-center gap-2">
          {isLoading ? (
            <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
          ) : null}
          <span className="truncate">{triggerText}</span>
        </span>
        {triggerChevronVariant === "chevron" ? (
          <ChevronDown
            size={14}
            className={cn("firm-chevron shrink-0 text-text-disabled", open && "rotated")}
          />
        ) : (
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        )}
      </button>
    );
  };

  return (
    <Popover modal={popoverModal} open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{trigger ?? renderTrigger()}</PopoverTrigger>
      <PopoverContent
        className={cn(
          contentClassName ?? "w-[--radix-popover-trigger-width]",
          "p-0",
          // Held open beneath a modal dialog (see `holdOpen`): stay mounted so the query survives, but hidden.
          holdOpen && "invisible"
        )}
        align="start"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <Command key={commandResetNonce} shouldFilter={false}>
          <CommandInput placeholder={searchPlaceholder} onValueChange={onQueryChange} />
          <CommandList>
            {isLoading ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">Loading...</div>
            ) : (
              <>
                {!showCreate && filteredItems.length === 0 && (
                  <CommandEmpty>{commandEmptyLabel}</CommandEmpty>
                )}
                {renderItems()}
                {showCreate && (
                  <CommandItem
                    value={`__create__${createLabel}`}
                    onSelect={() => {
                      void onCreate();
                    }}
                    className="m-1 cursor-pointer border border-dashed border-primary-border text-primary-emphasis"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Create &quot;{createLabel}&quot;
                  </CommandItem>
                )}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
